import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import validate from 'validate.js';
import { DialogContentText, Grid, Typography } from '@mui/material';
import moment from 'moment';
import {
  DialogDefaultV2,
  InputV2,
  DefaultSelectV2,
  NumberInputV2,
  TrueFalseSelectV2,
  ArquivoContainerV2,
  DatePickerV2,
  UsuarioSelectV2,
} from '../../../components/v2';
import * as selectorsControleCaixas from '../../../store/controleCaixa/reducer';
import * as actionsControleCaixas from '../../../store/controleCaixa/actions';
import * as selectorsAuth from '../../../store/auth/reducer';

const schema = {
  data: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  possuiNotaFiscal: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  tipoDespesa: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  formaPagamento: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  valor: { presence: { allowEmpty: false, message: 'é obrigatório' } },
};

const TIPO_DESPESA_OPTIONS = [
  { id: 'ALUGUEL_GARAGEM', nome: 'Aluguel de Garagem' },
  { id: 'ALUGUEL_VEICULOS', nome: 'Aluguel de Veículo' },
  { id: 'APORTE', nome: 'Aporte' },
  { id: 'CONDOMINIO', nome: 'Condomínio' },
  { id: 'DARF', nome: 'Darf' },
  { id: 'ENERGIA_ELETRICA', nome: 'Energia Elétrica' },
  { id: 'FOLHA_PAGAMENTO', nome: 'Folha Pagamento' },
  { id: 'HONORARIOS_CONTABEIS', nome: 'Honorários Contábeis' },
  { id: 'IMPOSTOS', nome: 'Impostos' },
  { id: 'INTERNET', nome: 'Internet' },
  { id: 'MANUTENCAO_DRONE', nome: 'Manutenção Drone' },
  { id: 'MANUTENCAO_VEICULO', nome: 'Manutenção Veículo' },
  { id: 'OUTROS', nome: 'Outros' },
  { id: 'PAGAMENTO_DIGITACAO', nome: 'Pagamento Digitação' },
  { id: 'PAGAMENTO_PRODUCAO', nome: 'Pagamento Produção' },
  { id: 'PLANO_SAUDE', nome: 'Plano de Saúde' },
  { id: 'SEGURO_CONTRA_ACIDENTE', nome: 'Seguro Contra Acidente' },
  { id: 'TAXAS', nome: 'Taxas' },
];

const USUARIO_REQUIRED_TIPOS = ['APORTE', 'FOLHA_PAGAMENTO', 'PAGAMENTO_DIGITACAO', 'PAGAMENTO_PRODUCAO'];

const FormularioDespesas = ({ saveControleCaixa, updateControleCaixa, selectedControleCaixa, open, id, onClose, findControleCaixaById, authData, cancelarControleCaixa, liquidarControleCaixa }) => {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [openCancelar, setOpenCancelar] = useState(false);
  const [openLiquidar, setOpenLiquidar] = useState(false);
  const [openALiquidar, setOpenALiquidar] = useState(false);

  useEffect(() => {
    if (open && !id) {
      setValues({
        criadoPorId: authData.id,
        criadoPorName: authData.nome,
        data: moment().valueOf(),
        dataCriacaoFormat: moment().format('DD/MM/YYYY HH:mm'),
        status: 'A_LIQUIDAR',
      });
    }
  }, [open, authData, id]);

  useEffect(() => {
    if (id) findControleCaixaById(id, 'despesas');
  }, [id, findControleCaixaById]);

  useEffect(() => {
    if (id && selectedControleCaixa && id === selectedControleCaixa.id) {
      setValues(selectedControleCaixa);
    }
  }, [id, selectedControleCaixa]);

  const readOnly = values.cancelada || values.status === 'LIQUIDADO';

  const onSave = () => {
    let erros = validate(values, schema);
    if (USUARIO_REQUIRED_TIPOS.includes(values.tipoDespesa) && !values.usuarioId) {
      erros = { ...(erros || {}), usuarioId: true };
    }
    setErrors(erros || {});
    if (!erros) {
      const callback = () => onCloseLocal(true);
      if (values.id) updateControleCaixa(values, 'despesas', callback);
      else saveControleCaixa(values, 'despesas', callback);
    }
  };

  const onCloseLocal = (isSave) => {
    setErrors({});
    onClose(isSave);
  };

  const handleChange = (event) => {
    setValues({ ...values, [event.target.name]: event.target.value });
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={() => onCloseLocal(false)}
      handlerConfirm={onSave}
      title="Despesa"
      cancelMessage="Fechar"
      confirmMessage="Salvar"
      readOnly={readOnly}
      maxWidth="md"
      headerAction={[
        {
          onClick: () => setOpenCancelar(true),
          title: 'Cancelar',
          show: (values.cancelada === undefined || values.cancelada === false) && values.id !== undefined && values.status === 'A_LIQUIDAR',
        },
        {
          onClick: () => setOpenLiquidar(true),
          title: 'Liquidar',
          show: values.status === 'A_LIQUIDAR' && values.id !== undefined && values.cancelada === false,
        },
        {
          onClick: () => setOpenALiquidar(true),
          title: 'A Liquidar',
          show: values.status === 'LIQUIDADO' && values.id !== undefined && values.cancelada === false,
        },
      ]}
    >
      <Grid container spacing={3}>
        {values.cancelada && (
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ color: 'error.main', fontStyle: 'italic' }}>
              **** Despesa Cancelada por {values.modificadoPorName} em {values.dataUltimaAtualizacaoFormat}
            </Typography>
          </Grid>
        )}
        {values.status === 'LIQUIDADO' && (
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ color: 'error.main', fontStyle: 'italic' }}>
              **** Despesa Liquidada por {values.modificadoPorName} em {values.dataUltimaAtualizacaoFormat}
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 4 }}>
          <DatePickerV2
            error={!!(errors && errors.data)}
            isRequired
            label="Data da Despesa"
            onChange={(data) => setValues({ ...values, data: data.valueOf() })}
            value={values.data}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TrueFalseSelectV2
            error={!!(errors && errors.possuiNotaFiscal)}
            isRequired
            label="Possui Nota Fiscal"
            name="possuiNotaFiscal"
            onChange={handleChange}
            value={values.possuiNotaFiscal}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <DefaultSelectV2
            label="Status"
            options={[{ id: 'A_LIQUIDAR', nome: 'A Liquidar' }, { id: 'LIQUIDADO', nome: 'Liquidado' }]}
            value={values.status}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <DefaultSelectV2
            error={!!(errors && errors.tipoDespesa)}
            isRequired
            label="Tipo"
            name="tipoDespesa"
            showEmpty
            options={TIPO_DESPESA_OPTIONS}
            onSelect={(event) => setValues({ ...values, [event.target.name]: event.target.value, usuarioId: null })}
            value={values.tipoDespesa}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <UsuarioSelectV2
            ativo
            error={!!(errors && errors.usuarioId)}
            onSelect={(usuarioId) => setValues({ ...values, usuarioId })}
            value={values.usuarioId}
            disabled={readOnly || !values.tipoDespesa || !USUARIO_REQUIRED_TIPOS.includes(values.tipoDespesa)}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <DefaultSelectV2
            error={!!(errors && errors.formaPagamento)}
            isRequired
            label="Forma Pagamento"
            name="formaPagamento"
            showEmpty
            options={[{ id: 'BANCO', nome: 'Banco' }, { id: 'CAIXA', nome: 'Caixa' }]}
            onSelect={handleChange}
            value={values.formaPagamento}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <NumberInputV2
            error={!!(errors && errors.valor)}
            isRequired
            label="Valor"
            onChange={(valor) => setValues({ ...values, valor })}
            value={values.valor}
            format="##,######"
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputV2 label="Criado Por" disabled value={values.criadoPorName} />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputV2 label="Data Criação" disabled value={values.dataCriacaoFormat} />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <InputV2 label="Data Última Atualização" disabled value={values.dataUltimaAtualizacaoFormat} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2 label="Descrição" name="descricao" onChange={handleChange} value={values.descricao} rows={3} multiline disabled={readOnly} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <InputV2 label="Observação" name="observacao" onChange={handleChange} value={values.observacao} rows={3} multiline disabled={readOnly} />
        </Grid>
        <ArquivoContainerV2
          arquivos={values.arquivos || []}
          onSave={(arquivos) => setValues({ ...values, arquivos })}
          disabled={readOnly}
        />
      </Grid>
      <DialogDefaultV2
        open={openCancelar}
        handlerCancel={() => setOpenCancelar(false)}
        handlerConfirm={() => { setOpenCancelar(false); cancelarControleCaixa(values.id, 'despesas', () => onCloseLocal(true)); }}
        title="Confirmação"
        confirmMessage="Sim"
        cancelMessage="Não"
      >
        <DialogContentText>Deseja realmente cancelar a despesa?</DialogContentText>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={openLiquidar}
        handlerCancel={() => setOpenLiquidar(false)}
        handlerConfirm={() => { setOpenLiquidar(false); liquidarControleCaixa(values.id, 'despesas', 'LIQUIDADO', () => onCloseLocal(true)); }}
        title="Confirmação"
        confirmMessage="Sim"
        cancelMessage="Não"
      >
        <DialogContentText>Deseja realmente liquidar a despesa?</DialogContentText>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={openALiquidar}
        handlerCancel={() => setOpenALiquidar(false)}
        handlerConfirm={() => { setOpenALiquidar(false); liquidarControleCaixa(values.id, 'despesas', 'A_LIQUIDAR', () => onCloseLocal(true)); }}
        title="Confirmação"
        confirmMessage="Sim"
        cancelMessage="Não"
      >
        <DialogContentText>Deseja realmente alterar o status para 'A Liquidar'?</DialogContentText>
      </DialogDefaultV2>
    </DialogDefaultV2>
  );
};

const mapStateToProps = (state) => ({
  selectedControleCaixa: selectorsControleCaixas.getSelectedControleCaixa(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsControleCaixas }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(FormularioDespesas);
